import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { SCORED_QUESTIONS, TOTAL_QUESTIONS } from './scoring';
import { useScore } from './ScoreContext';
import { createScoreResultUrl } from './scoreResultUrl';
import { trackScoreComplete } from '../lib/analytics';
export function Diagnostic() {
  const {
    answers,
    setAnswer,
    answeredCount,
    result,
    isComplete,
    isDiagnosticOpen,
    closeDiagnostic
  } = useScore();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Sync to the first unanswered question only when the panel opens.
  // Do NOT re-sync when answers change — that races the post-answer
  // auto-advance and skips questions (commonly Q2).
  useEffect(() => {
    if (!isDiagnosticOpen) return;
    const index = SCORED_QUESTIONS.findIndex(question => answers[question.id] === undefined);
    setCurrentIndex(index === -1 ? TOTAL_QUESTIONS - 1 : index);
    // intentionally only when open state flips to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDiagnosticOpen]);
  if (!isDiagnosticOpen) {
    return null;
  }
  const question = SCORED_QUESTIONS[currentIndex];
  const selectedScore = answers[question.id];
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < TOTAL_QUESTIONS - 1;
  const isLastQuestion = !canGoForward;
  const selectAnswer = (score: number) => {
    setAnswer(question.id, score);
    if (canGoForward) {
      window.setTimeout(() => setCurrentIndex(index => index + 1), 180);
    }
  };
  const showScore = () => {
    if (!isComplete || !result) {
      const missingIndex = SCORED_QUESTIONS.findIndex(item => answers[item.id] === undefined);
      setCurrentIndex(missingIndex === -1 ? TOTAL_QUESTIONS - 1 : missingIndex);
      return;
    }
    trackScoreComplete(result.totalScore, result.band);
    // Same-tab navigation — window.open is often blocked and feels like a dead button.
    window.location.assign(createScoreResultUrl(result));
  };
  return <div className="fixed inset-0 z-[60] overflow-y-auto bg-paper px-6 py-6 md:py-10" role="dialog" aria-modal="true" aria-labelledby="diagnostic-title" data-id="element-108" data-mp="2r4j0q36i26zc">
      <div className="mx-auto max-w-3xl" data-id="element-109" data-mp="311ou5i2l6a2q">
        <header className="flex items-center justify-between gap-4" data-id="element-110" data-mp="310m5zhwnelqi">
          <div data-id="element-111" data-mp="3611ik793vp7f">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-112" data-mp="361jibduz8lk3">
              Reader-to-Revenue Score
            </p>
            <p className="mt-1 text-sm font-medium text-text-muted" data-id="element-113" data-mp="3a09680dn5lhz">
              {answeredCount} of {TOTAL_QUESTIONS} answered
            </p>
          </div>
          <button type="button" onClick={closeDiagnostic} className="rounded-full p-3 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close diagnostic" data-id="element-114" data-mp="3jt7vzlkvji36">
            <X className="h-5 w-5" data-id="element-115" data-mp="3to9liij2am6a" />
          </button>
        </header>

        <div className="mt-6 flex gap-1.5" aria-hidden="true" data-id="element-116" data-mp="1mtmbytn1ual">
          {SCORED_QUESTIONS.map(item => <span key={item.id} className={`h-1.5 flex-1 rounded-full ${answers[item.id] === undefined ? 'bg-gray-200' : 'bg-seaglass'}`} data-id="element-117" data-mp="1m5vyqvl9d5ze" />)}
        </div>

        <main className="py-12 md:py-16" data-id="element-118" data-mp="1d3ee2ll94lei">
          <p className="text-sm font-medium text-text-muted" data-id="element-119" data-mp="1d466xongs513">
            Question {currentIndex + 1} of {TOTAL_QUESTIONS}
          </p>
          <h1 id="diagnostic-title" className="mt-4 max-w-3xl font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-120" data-mp="25sbt8fu62231">
            {question.prompt}
          </h1>

          <div className="mt-9 flex flex-col gap-3" data-id="element-121" data-mp="20tqrgbz9nach">
            {question.options.map(option => {
            const isSelected = selectedScore === option.score;
            return <button key={option.letter} type="button" aria-pressed={isSelected} onClick={() => selectAnswer(option.score)} className={`flex min-h-[72px] w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-seaglass md:p-6 ${isSelected ? 'border-seaglass bg-seaglass/10 ring-1 ring-seaglass/40' : 'border-gray-200 bg-white hover:border-seaglass/50'}`} data-id="element-122" data-mp="1r4752wccgycv">
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${isSelected ? 'border-seaglass bg-seaglass text-white' : 'border-gray-300 text-navy'}`} data-id="element-123" data-mp="28cgccxqwk687">
                    {isSelected ? <Check className="h-4 w-4" data-id="element-124" data-mp="2n0ktvye7ybpe" /> : option.letter}
                  </span>
                  <span className="leading-relaxed text-text" data-id="element-125" data-mp="2n2x8q2ook5y8">{option.text}</span>
                </button>;
          })}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4" data-id="element-126" data-mp="2db9puq66vx8d">
            <button type="button" onClick={() => setCurrentIndex(index => index - 1)} disabled={!canGoBack} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-127" data-mp="2i9uowm9pgav8">
              <ArrowLeft className="h-4 w-4" data-id="element-128" data-mp="3as8exzosusjr" />
              Back
            </button>

            {isLastQuestion ? <div className="flex flex-col items-end gap-2" data-id="element-129" data-mp="z8tr8lhcdxlc">
                <button type="button" onClick={showScore} disabled={selectedScore === undefined || !isComplete} className="inline-flex min-h-[52px] items-center gap-2 rounded-xl bg-[#d4a574] px-6 py-3 font-bold text-white transition-colors hover:bg-[#c89860] focus:outline-none focus:ring-2 focus:ring-seaglass disabled:cursor-not-allowed disabled:opacity-40" data-id="element-130" data-mp="ubafptc13yx3">
                  See my score
                  <ArrowRight className="h-4 w-4" data-id="element-131" data-mp="beizjsrs9zx9" />
                </button>
                {selectedScore !== undefined && !isComplete && <p className="max-w-xs text-right text-sm text-text-muted" data-id="element-132" data-mp="peaa1jj3cy6i">
                    One earlier question is still unanswered. Use Back to finish
                    it.
                  </p>}
              </div> : <button type="button" onClick={() => setCurrentIndex(index => index + 1)} disabled={selectedScore === undefined} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-133" data-mp="20u8mlxdha2ch">
                Next question
                <ArrowRight className="h-4 w-4" data-id="element-134" data-mp="12ftc6mzvjens" />
              </button>}
          </div>
        </main>
      </div>
    </div>;
}